import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { IContractRequirement, IContractRequirementItem, IScheduleSummary } from '@/store/types';

import { classList } from '@/helpers';
import {
  Card,
  ContractRequirementsSkeleton,
  ContractRequirementsSkeletonFullWidth,
  LoaderWrapper,
  RequirementText,
} from '@/components/common';
import { ContractRequirements as ContractRequirementsSvg } from '@/assets/icons';
import { MAX_CONTRACT_DETAILS_WIDGET_HEIGHT } from '@/config/variables';
import { RequirementType } from '@/store/enums';
import { media } from '@/styles/theme';

interface IProps {
  contractSchedules: IScheduleSummary[] | null;
  contractRequirements?: IContractRequirement;
  fullWidth?: boolean;
  isLoading?: boolean;
}

const ContractRequirements = ({
  contractRequirements,
  fullWidth,
  isLoading = false,
  contractSchedules = [],
}: IProps): React.ReactElement => {
  const { t } = useTranslation();

  const contractMinimum: IContractRequirementItem[] = useMemo(
    () =>
      contractRequirements?.contractRequirements?.filter(
        requirement => requirement.requirementType === RequirementType.min,
      ) ?? [],
    [contractRequirements],
  );
  const contractMaximum: IContractRequirementItem[] = useMemo(
    () =>
      contractRequirements?.contractRequirements?.filter(
        requirement => requirement.requirementType === RequirementType.max,
      ) ?? [],
    [contractRequirements],
  );
  const contractBase: IContractRequirementItem | undefined = useMemo(
    () =>
      contractRequirements?.contractRequirements?.find(
        requirement => requirement.requirementType === RequirementType.base,
      ),
    [contractRequirements],
  );
  const contractPeak: IContractRequirementItem | undefined = useMemo(
    () =>
      contractRequirements?.contractRequirements?.find(
        requirement => requirement.requirementType === RequirementType.peak,
      ),
    [contractRequirements],
  );

  return (
    <StyledLoaderWrapper
      fullWidth={fullWidth}
      isLoading={isLoading}
      loader={fullWidth ? <ContractRequirementsSkeletonFullWidth /> : <ContractRequirementsSkeleton />}
    >
      <Container className={classList(fullWidth && 'full-width')}>
        <Card
          name={t`client.gigs.gigRequirements.title`}
          icon={ContractRequirementsSvg}
          maxBodyHeight={!fullWidth ? MAX_CONTRACT_DETAILS_WIDGET_HEIGHT : undefined}
          bodyHeight={!fullWidth ? MAX_CONTRACT_DETAILS_WIDGET_HEIGHT : undefined}
        >
          <Table>
            {!!contractMinimum.length && (
              <Row>
                <Cell>
                  <Label>{t`client.gigs.gigRequirements.labelMinimum`}</Label>
                  {contractMinimum.map(min => (
                    <RequirementText
                      key={min.contractRequirementId}
                      requirement={min}
                      contractSchedules={contractSchedules}
                    />
                  ))}
                </Cell>
              </Row>
            )}
            {!!contractMaximum.length && (
              <Row>
                <Cell>
                  <Label>{t`client.gigs.gigRequirements.labelMaximum`}</Label>
                  {contractMaximum.map(max => (
                    <RequirementText
                      key={max.contractRequirementId}
                      requirement={max}
                      contractSchedules={contractSchedules}
                    />
                  ))}
                </Cell>
              </Row>
            )}
            {contractBase && (
              <Row>
                <Cell>
                  <Label>{t`client.gigs.gigRequirements.labelBase`}</Label>
                  <RequirementText requirement={contractBase} contractSchedules={contractSchedules} />
                </Cell>
              </Row>
            )}
            {!!contractPeak && (
              <Row>
                <Cell>
                  <Label>{t`client.gigs.gigRequirements.labelPeak`}</Label>
                  <RequirementText requirement={contractPeak} contractSchedules={contractSchedules} />
                </Cell>
              </Row>
            )}
          </Table>
        </Card>
      </Container>
    </StyledLoaderWrapper>
  );
};

export { ContractRequirements };

const Container = styled.div`
  width: 100%;

  ${media.tablet} {
    margin: 16px 9px;
    width: 50%;

    &.full-width {
      margin: 0;
      width: 100%;
    }
  }
`;

const StyledLoaderWrapper = styled(LoaderWrapper)<{ fullWidth: boolean | undefined }>`
  ${media.tablet} {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : '50%')};
  }
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-collapse: collapse;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  &:not(:last-child) {
    border-bottom: 1px solid #dee2e6;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px 14px;

  &:last-child {
    padding-right: 10px;
  }
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: bold;
  line-height: 18px;
  color: #5a5a5a;
  overflow: hidden;
`;
